'use client'

import type { ReviewListItem } from '@mentora/data-access'
import type { SubmitReview } from './review-form'
import { useFormatter, useTranslations } from 'next-intl'
import { ReviewForm } from './review-form'
import { StarRating } from './star-rating'

interface ReviewSectionProps {
  courseId: string
  orgSlug: string
  courseSlug: string
  summary: { average: number, count: number }
  reviews: ReviewListItem[]
  canReview: boolean
  userReview: { rating: number, body: string | null } | null
  submitAction: SubmitReview
}

export function ReviewSection({
  courseId,
  orgSlug,
  courseSlug,
  summary,
  reviews,
  canReview,
  userReview,
  submitAction,
}: ReviewSectionProps) {
  const t = useTranslations('reviews')
  const format = useFormatter()

  return (
    <section className="mt-10">
      <h2 className="text-xl font-semibold tracking-tight text-foreground">
        {t('title')}
      </h2>

      <div className="mt-3 flex items-center gap-3">
        {summary.count > 0
          ? (
              <>
                <span className="tnum text-2xl font-semibold text-foreground">
                  {summary.average.toFixed(1)}
                </span>
                <StarRating value={summary.average} size={18} />
                <span className="text-sm text-muted-foreground">
                  {t('count', { count: summary.count })}
                </span>
              </>
            )
          : (
              <span className="text-sm text-muted-foreground">
                {t('noneYet')}
              </span>
            )}
      </div>

      {canReview && (
        <div className="mt-5">
          <ReviewForm
            courseId={courseId}
            orgSlug={orgSlug}
            courseSlug={courseSlug}
            initial={userReview}
            submitAction={submitAction}
          />
        </div>
      )}

      {reviews.length > 0 && (
        <ul className="mt-6 flex flex-col gap-5">
          {reviews.map(r => (
            <li
              key={r.id}
              className="border-b border-border/60 pb-5 last:border-0"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-foreground">
                  {r.reviewerName ?? t('anonymous')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {format.dateTime(new Date(r.createdAt), {
                    dateStyle: 'medium',
                  })}
                </span>
              </div>
              <StarRating value={r.rating} size={14} className="mt-1.5" />
              {r.body && (
                <p className="mt-2 whitespace-pre-line text-sm text-muted-foreground">
                  {r.body}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
